'use client';

import { FC } from 'react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useLiftChatAbove } from '@/hotdesk-ui/hooks/compare/useLiftChatAbove';
import { CompareExitMethod, CompareRemoveSource, CompareType } from '@/hotdesk-ui/enum/compare';
import type { ExploreCompare } from '@/hotdesk-ui/hooks/compare/useExploreCompare';
import styles from './CompareFloatingBar.module.scss';
interface Props {
  compare: ExploreCompare;
}

/**
 * Bar pinned to the bottom of the Explore page while compare mode is on:
 * count, picked names (each removable), empty slots, Cancel and the compare
 * button. The button stays off until 2 are picked. Copy names what is being
 * compared: workspaces in the Workspaces view, listings under a listing type.
 */
const CompareFloatingBar: FC<Props> = ({
  compare
}) => {
  const t = useTranslations('compare');
  const barRef = useLiftChatAbove<HTMLDivElement>();
  const {
    type,
    picks,
    maxItems,
    atLimit,
    canCompare
  } = compare;
  const count = picks.length;
  const emptySlots = Math.max(maxItems - count, 0);
  const isListing = type === CompareType.LISTING;
  const title = count === 0 ? t(isListing ? 'explore.pickUpToListings' : 'explore.pickUpToWorkspaces', {
    max: maxItems
  }) : t('explore.selected', {
    count,
    max: maxItems
  });
  const subtitle = count === 0 ? t('explore.seeSideBySide') : atLimit ? t('limitReached', {
    max: maxItems
  }) : t('explore.addMore', {
    count: emptySlots
  });
  return <div ref={barRef} className={styles.bar} role="region" aria-label={t('button')} data-sentry-component="CompareFloatingBar" data-sentry-source-file="CompareFloatingBar.tsx">
            <div className={styles.status}>
                <p className={styles.title} aria-live="polite">
                    {title}
                </p>
                <p className={styles.subtitle}>{subtitle}</p>
            </div>

            <ul className={styles.picks}>
                {picks.map(pick => <li key={pick.id} className={styles.chip}>
                        <span className={styles.chipName} title={pick.name} dir="auto">
                            {pick.name}
                        </span>
                        <button type="button" className={styles.chipRemove} aria-label={t('explore.remove', {
          name: pick.name
        })} onClick={() => compare.remove(pick.id, CompareRemoveSource.EXPLORE_BAR)}>
                            <X size={10} strokeWidth={3} />
                        </button>
                    </li>)}
                {Array.from({
        length: emptySlots
      }, (_, i) => <li key={`slot-${i}`} className={styles.slot} aria-hidden="true" />)}
            </ul>

            <div className={styles.actions}>
                <button type="button" className={styles.cancel} onClick={() => compare.exit(CompareExitMethod.CANCEL)}>
                    {t('explore.cancel')}
                </button>
                <button type="button" className={styles.compareBtn} disabled={!canCompare} onClick={compare.openComparison}>
                    {count === 0 ? t('button') : t(isListing ? 'explore.compareListings' : 'explore.compareWorkspaces', {
          count
        })}
                </button>
            </div>
        </div>;
};
export default CompareFloatingBar;